import { AdvancedTrackDefinition } from '../../api/types'
import { humanizeRole } from '../../format'
import { plannerTrackColors } from '../../theme'

export const MONTHS = 6
export const MONTH_LABELS = Array.from({ length: MONTHS }, (_, index) => `Month ${index + 1}`)

// The table's real columns are weeks, because Surveys renders at week granularity; every other
// row's month cell spans this many of them.
export const WEEKS_PER_MONTH = 4
export const TOTAL_WEEKS = MONTHS * WEEKS_PER_MONTH

export type Subcategory = { key: string; label: string }
export type Category = { id: string; label: string; subcategories: Subcategory[] }

export const rowKeyFor = (categoryId: string, subcategory?: string) =>
  subcategory ? `${categoryId}.${subcategory.toLowerCase()}` : categoryId

const subcategoriesOf = (categoryId: string, labels: string[]): Subcategory[] =>
  labels.map((label) => ({ key: rowKeyFor(categoryId, label), label }))

export const MODULES_CATEGORY_ID = 'modules'

// Built into advanced/service's catalog (ADR-098); its bar runs the whole plan (ADR-099).
export const PSYCHIATRIC_SUPERVISION_TRACK_KEY = 'psychiatric-supervision'
export const PSYCHIATRY_ROW_KEY = `${MODULES_CATEGORY_ID}.psychiatry`

// `:` is not identifier-safe, so no CMS module key can collide with this row.
const STANDALONE_TRACKS_ROW_KEY = `${MODULES_CATEGORY_ID}:standalone`
export const DEFAULT_TRACK_MONTHS = 3

export type TrackRow = { key: string; label: string; tracks: AdvancedTrackDefinition[] }

// CMS modules the care plan no longer offers; their tracks still exist in the catalog.
export const RETIRED_MODULES = ['activity']

// One row per CMS module, then one for standalone tracks and any live key the catalog lacks (ADR-092).
export const trackRowsFor = (catalog: AdvancedTrackDefinition[], liveTrackKeys: string[] = []): TrackRow[] => {
  // A retired module keeps a row only for tracks the patient is or was on, so they can still be ended there.
  const offered = catalog.filter(
    (track) => !track.module || !RETIRED_MODULES.includes(track.module) || liveTrackKeys.includes(track.trackKey)
  )
  const modules = [...new Set(offered.flatMap((track) => (track.module ? [track.module] : [])))]
  const unknown = [...new Set(liveTrackKeys)]
    .filter((trackKey) => !catalog.some((track) => track.trackKey === trackKey))
    .map((trackKey) => ({ trackKey, title: trackKey, module: null, ownerRole: null }))
  const standalone = [...offered.filter((track) => !track.module), ...unknown]
  const rows = modules.map((module) => ({
    key: `${MODULES_CATEGORY_ID}.${module}`,
    label: humanizeRole(module),
    tracks: offered.filter((track) => track.module === module),
  }))

  return standalone.length > 0
    ? [...rows, { key: STANDALONE_TRACKS_ROW_KEY, label: 'Other tracks', tracks: standalone }]
    : rows
}

export const isTrackRowKey = (rowKey: string) =>
  rowKey.startsWith(`${MODULES_CATEGORY_ID}.`) || rowKey === STANDALONE_TRACKS_ROW_KEY

const STATIC_CATEGORIES: Category[] = [
  { id: 'tracking', label: 'Tracking', subcategories: subcategoriesOf('tracking', ['Labs']) },
]

export const categoriesFor = (trackRows: TrackRow[]): Category[] => [
  {
    id: MODULES_CATEGORY_ID,
    label: 'Modules',
    subcategories: trackRows.map(({ key, label }) => ({ key, label })),
  },
  ...STATIC_CATEGORIES,
]

export type PlannerRow = { key: string; label: string; indented: boolean; categoryId?: string; expandable: boolean }

export const visibleRows = (categories: Category[], expandedCategoryIds: string[]): PlannerRow[] =>
  categories.flatMap((category) => {
    const expandable = category.subcategories.length > 0
    const parentRow: PlannerRow = {
      key: rowKeyFor(category.id),
      label: category.label,
      indented: false,
      categoryId: category.id,
      expandable,
    }

    if (!expandable || !expandedCategoryIds.includes(category.id)) {
      return [parentRow]
    }

    return [
      parentRow,
      ...category.subcategories.map((subcategory) => ({
        key: subcategory.key,
        label: subcategory.label,
        indented: true,
        expandable: false,
      })),
    ]
  })

// A row absent from `ROW_INTERACTIONS` keeps a plain free-text cell.
export type RowInteraction =
  | { kind: 'options'; options: string[]; defaultMonths?: number }
  | { kind: 'single'; label: string; months: number }
  | { kind: 'tracks'; tracks: AdvancedTrackDefinition[] }

const ROW_INTERACTIONS: Record<string, RowInteraction> = {
  [rowKeyFor('tracking', 'Surveys')]: { kind: 'options', options: ['PHQ-9', 'GAD-7', 'ISI', 'ASRM', 'OCI-R', 'PCL-5'] },
  [rowKeyFor('tracking', 'Wearables')]: { kind: 'options', options: ['Sleep Apnea Screener', 'Continuous Wearable'] },
  [rowKeyFor('tracking', 'Labs')]: { kind: 'options', options: ['Advanced Panel', 'Custom Bloodwork', 'Other Labs'] },
}

export const DEFAULT_ITEM_MONTHS = 1

export const interactionFor = (rowKey: string, trackRows: TrackRow[] = []): RowInteraction | undefined => {
  const trackRow = trackRows.find((row) => row.key === rowKey)

  return trackRow ? { kind: 'tracks', tracks: trackRow.tracks } : ROW_INTERACTIONS[rowKey]
}

export const defaultMonthsFor = (rowKey: string): number => {
  if (isTrackRowKey(rowKey)) {
    return DEFAULT_TRACK_MONTHS
  }
  const interaction = ROW_INTERACTIONS[rowKey]
  if (interaction?.kind === 'single') {
    return interaction.months
  }
  if (interaction?.kind === 'options') {
    return interaction.defaultMonths ?? DEFAULT_ITEM_MONTHS
  }
  return DEFAULT_ITEM_MONTHS
}

// A label must match its row's `ROW_INTERACTIONS` option exactly; a track item matches by `trackKey` instead.
export type ChecklistItem = { label: string; rowKey: string; trackKey?: string }
export type ChecklistSection = { title: string; items: ChecklistItem[] }

const STATIC_CHECKLIST_SECTIONS: ChecklistSection[] = [
  {
    title: 'Diagnostics',
    items: [
      { label: 'Advanced Panel', rowKey: rowKeyFor('tracking', 'Labs') },
      { label: 'Sleep Apnea Screener', rowKey: rowKeyFor('tracking', 'Wearables') },
      { label: 'Custom Bloodwork', rowKey: rowKeyFor('tracking', 'Labs') },
      { label: 'Other Labs', rowKey: rowKeyFor('tracking', 'Labs') },
    ],
  },
  {
    title: 'Diagnostic Surveys',
    items: [
      { label: 'PHQ-9', rowKey: rowKeyFor('tracking', 'Surveys') },
      { label: 'GAD-7', rowKey: rowKeyFor('tracking', 'Surveys') },
      { label: 'ISI', rowKey: rowKeyFor('tracking', 'Surveys') },
      { label: 'ASRM', rowKey: rowKeyFor('tracking', 'Surveys') },
      { label: 'OCI-R', rowKey: rowKeyFor('tracking', 'Surveys') },
      { label: 'PCL-5', rowKey: rowKeyFor('tracking', 'Surveys') },
    ],
  },
]

export const checklistSectionsFor = (trackRows: TrackRow[]): ChecklistSection[] => {
  const [diagnostics, surveys] = STATIC_CHECKLIST_SECTIONS
  // Psychiatric supervision is on every master plan already (`ensurePsychiatricSupervision`), so it is not a choice.
  const trackSections = trackRows
    .filter((row) => row.key !== PSYCHIATRY_ROW_KEY)
    .map((row) => ({
      title: row.label,
      items: row.tracks.map((track) => ({ label: track.title, rowKey: row.key, trackKey: track.trackKey })),
    }))

  return [diagnostics, ...trackSections, surveys]
}

export const colorForOption = (option: string) => {
  const hash = option.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
  return plannerTrackColors[hash % plannerTrackColors.length]
}
